import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as api from 'config/axios';
import { CustomNotification } from 'common/components';
import * as globalActions from '@redux/global/actions';
import * as endpoints from 'services/assetCertificates/endpoints';
import * as actionsGridView from 'common/components/grid-view/controllers/actions';
import userSelector from '@redux/user/selectors';
import { forwardTo } from 'utils/common/route';
import * as certActions from '../../controllers/actions';
import * as selectors from '../../controllers/selectors';
import { Modal } from 'antd';
const { confirm } = Modal;

export const useCertificateActions = (isDetailPage = false) => {
  const dispatch = useDispatch();
  let userInfo = useSelector(userSelector.makeSelectUserInfo());
  const memberId = userInfo?.member.id;
  const [modalState, setModalState] = useState({
    visible: false,
    mode: 'add',
    data: null,
  });
  const [loading, setLoading] = useState(false);

  const certificateFullData = useSelector(selectors.makeSelectCertificateData());
  const loadingDetail = useSelector(selectors.makeSelectLoadingDetail());
  const handleAdd = () => {
    setModalState({ visible: true, mode: 'add', data: null });
  };

  const handleEdit = (selectedItem) => {
    if (!selectedItem) {
      CustomNotification.warning('Please select a certificate to edit');
      return;
    }
    setModalState({ visible: true, mode: 'edit', data: selectedItem });
  };

  const handleDelete = async (selectedItem) => {
    if (!selectedItem?.id) {
      CustomNotification.warning('Please select a certificate to delete');
      return;
    }

    confirm({
      title: 'Are you sure you want to delete this certificate?',
      content: `Certificate: ${selectedItem.certificateName || 'Unnamed Certificate'}`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      centered: true,
      onOk: async () => {
        try {
          const response = await api.sendDelete(
            endpoints.CERTIFICATE_DELETE_ENDPOINT + `/${selectedItem.id}`
          );

          if (response?.isSuccess) {
            CustomNotification.success('Certificate deleted successfully');
            // Refresh or redirect
            if (isDetailPage) {
              forwardTo(`/digital-media/certificates`);
            } else {
              await dispatch(actionsGridView.updateIsRefreshGrid());
            }
          } else {
            CustomNotification.error(response?.message || 'Failed to delete certificate');
          }
        } catch (error) {
          console.error('Error deleting certificate:', error);
          CustomNotification.error('Error deleting certificate');
        }
      },
    });
  };

  const handleSubmit = async (formValues) => {
    try {
      const { endpoint, payload } = buildCertificatePayload(modalState, formValues, endpoints);
      const response = await api.sendPost(endpoint, payload);

      if (response?.isSuccess) {
        CustomNotification.success(
          modalState.mode === 'add'
            ? 'Certificate created successfully'
            : 'Certificate updated successfully'
        );

        if (isDetailPage) {
          modalState.mode === 'add'
            ? forwardTo(`/certificate/${response?.data?.certificateId}`)
            : dispatch(certActions.getCertificateFullDetail(payload.id));
        } else {
          dispatch(actionsGridView.updateIsRefreshGrid());
        }

        setModalState({ visible: false, mode: 'add', data: null });
        dispatch(actionsGridView.deleteItemsSelection());
        dispatch(actionsGridView.updatePagesSelection([]));
        dispatch(actionsGridView.updateItemPageSelection([]));
      } else {
        CustomNotification.error('Failed to save certificate');
      }
    } catch (error) {
      console.error('Error saving certificate:', error);
      CustomNotification.error('Error while saving certificate');
    }
  };

  const handleDownload = (certificateData) => {
    if (!certificateData) {
      CustomNotification.warning('Please select a certificate to download');
      return;
    }
    setLoading(true);
    dispatch(certActions.getCertificateFullDetail(certificateData?.id));
  };

  useEffect(() => {
    if (!loadingDetail && loading) {
      setLoading(false);
      if (certificateFullData?.drm) {
        showDrmNotify({ namingType: 'original' });
      } else {
        downloadFile({ namingType: 'original', certificateFullData });
      }
    }
  }, [loadingDetail, certificateFullData]);

  const downloadFile = ({ namingType, certificateFullData }) => {
    if (namingType === 'original') {
      downloadOriginalFile(certificateFullData);
      return;
    }
    downloadFileWithNamingType(namingType, certificateFullData);
  };

  const downloadOriginalFile = (certificateFullData) => {
    const url = certificateFullData?.metadata?.thumbModel?.originalFile;
    if (!url) {
      CustomNotification.warning('No file available for download');
      return;
    }

    setLoading(true);
    sendDownloadRequest(url);
  };

  const downloadFileWithNamingType = (namingType, certificateData) => {
    // Example for future extensibility (different naming styles)
    const url = certificateData?.metadata?.thumbModel?.[namingType];
    if (!url) {
      CustomNotification.warning('No file URL found for selected format');
      return;
    }

    setLoading(true);
    sendDownloadRequest(url);
  };

  const sendDownloadRequest = (url) => {
    api
      .sendDownload({ url })
      .then(() => {
        CustomNotification.success('Download completed successfully');
      })
      .catch(() => {
        CustomNotification.error('Failed to download file');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const showDrmNotify = ({ namingType }) => {
    CustomNotification.warning(
      `This file is DRM protected and cannot be downloaded directly. (${namingType})`
    );
  };


  function buildCertificatePayload(modalState, formValues, endpoints) {
    const commonData = {
      memberId: memberId || 0,
      certificateName: formValues.certificateName || '',
      certificateBody: formValues.certificateBody || '',
      level: formValues.level || '',
      type: formValues.type || '',
      status: formValues.status || '',
      expirationDate: formValues.expirationDate || '',
      description: formValues.description || '',
    };

    let endpoint;
    let payload;

    if (modalState.mode === 'add') {
      endpoint = endpoints.CERTIFICATE_CREATE_ENDPOINT;
      payload = {
        ...commonData,
        tempFileName: formValues.tempFileName || '',
      };
    } else {
      endpoint = endpoints.CERTIFICATE_UPDATE_ENDPOINT;
      payload = {
        ...commonData,
        id: formValues.id || modalState.data?.id,
      };
    }

    return { endpoint, payload };
  }

  return {
    modalState,
    setModalState,
    handleAdd,
    handleEdit,
    handleDelete,
    handleSubmit,
    handleDownload,
    loading,
  };
};
